/** PassagePlanningTab Component - Interactive passage planning */
import React, { useState, useMemo, useRef } from 'react';
import { Download, Navigation } from 'lucide-react';
import { Button } from '../../../catalyst/button';
import ManualWaypointModal from './ManualWaypointModal';
import PassageChart from './PassageChart';
import WaypointsList from './WaypointsList';
import PlanDetailsForm from './PlanDetailsForm';
import RouteSummary from './RouteSummary';
import { calculateDistance, calculateBearing, formatDuration, exportPassagePlan } from './utils/navigationUtils';
import DESIGN from '../designSystem';

const PassagePlanningTab = ({ 
  passagePlans = [],
  currentVessel,
  onSavePlan,
  onDeletePlan,
  onLinkToVoyage
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [waypoints, setWaypoints] = useState([]);
  const [planForm, setPlanForm] = useState({
    name: '',
    vesselSpeed: currentVessel?.cruisingSpeed || 8,
    notes: ''
  });
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualWaypoint, setManualWaypoint] = useState({ name: '', lat: '', lng: '', notes: '' });
  const [mapError, setMapError] = useState(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const routeLineRef = useRef(null);

  const routeStats = useMemo(() => {
    let totalDistance = 0;
    waypoints.forEach((wp, index) => {
      if (index > 0) {
        const prevWp = waypoints[index - 1];
        totalDistance += calculateDistance(prevWp.lat, prevWp.lng, wp.lat, wp.lng);
      }
    });
    const totalTime = planForm.vesselSpeed > 0 ? totalDistance / planForm.vesselSpeed : 0;
    return { totalDistance, totalTime, waypoints: waypoints.length };
  }, [waypoints, planForm.vesselSpeed]);

  const handleAddManualWaypoint = () => {
    if (!manualWaypoint.lat || !manualWaypoint.lng) {
      alert('Please enter waypoint coordinates');
      return;
    }
    const lat = parseFloat(manualWaypoint.lat);
    const lng = parseFloat(manualWaypoint.lng);
    if (isNaN(lat) || isNaN(lng)) {
      alert('Invalid coordinates');
      return;
    }

    const waypoint = { id: `wp_${Date.now()}`, name: manualWaypoint.name || `WP${waypoints.length + 1}`, lat, lng, notes: manualWaypoint.notes };

    if (waypoints.length > 0) {
      const lastWp = waypoints[waypoints.length - 1];
      waypoint.distance = calculateDistance(lastWp.lat, lastWp.lng, lat, lng);
      waypoint.bearing = calculateBearing(lastWp.lat, lastWp.lng, lat, lng);
    }

    setWaypoints([...waypoints, waypoint]);
    setManualWaypoint({ name: '', lat: '', lng: '', notes: '' });
    setShowManualEntry(false);
  };

  const removeWaypoint = (id) => {
    const newWaypoints = waypoints.filter(wp => wp.id !== id);
    const updatedWaypoints = newWaypoints.map((wp, index) => {
      if (index === 0) return { ...wp, distance: undefined, bearing: undefined };
      const prevWp = newWaypoints[index - 1];
      return {
        ...wp,
        distance: calculateDistance(prevWp.lat, prevWp.lng, wp.lat, wp.lng),
        bearing: calculateBearing(prevWp.lat, prevWp.lng, wp.lat, wp.lng)
      };
    });
    setWaypoints(updatedWaypoints);
  };

  const handleSavePlan = async () => {
    if (!planForm.name || waypoints.length < 2) {
      alert('Please enter a plan name and add at least 2 waypoints');
      return;
    }
    const plan = {
      id: selectedPlan?.id || `plan_${Date.now()}`,
      name: planForm.name,
      waypoints,
      vesselSpeed: planForm.vesselSpeed,
      totalDistance: routeStats.totalDistance,
      estimatedTime: routeStats.totalTime,
      notes: planForm.notes,
      vesselId: currentVessel?.id
    };
    const success = await onSavePlan(plan);
    if (success) {
      setIsEditing(false);
      setSelectedPlan(plan);
    }
  };
  const loadPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      name: plan.name,
      vesselSpeed: plan.vesselSpeed,
      notes: plan.notes || ''
    });
    setWaypoints(plan.waypoints || []);
    setIsEditing(false);
  };
  const handleMapReady = (mapInstance, markers, routeLine) => {
    mapInstanceRef.current = mapInstance;
    markersRef.current = markers || [];
    routeLineRef.current = routeLine;
  };
  const handleMapClick = (e, mapInstance) => {
    if (!window.L) return;
    const waypoint = {
      id: `wp_${Date.now()}`,
      name: `WP${waypoints.length + 1}`,
      lat: e.latlng.lat,
      lng: e.latlng.lng,
      notes: ''
    };
    if (waypoints.length > 0) {
      const lastWp = waypoints[waypoints.length - 1];
      waypoint.distance = calculateDistance(lastWp.lat, lastWp.lng, waypoint.lat, waypoint.lng);
      waypoint.bearing = calculateBearing(lastWp.lat, lastWp.lng, waypoint.lat, waypoint.lng);
    }
    setWaypoints([...waypoints, waypoint]);
  };
  const handleWaypointUpdate = (id, updates) => {
    const updatedWaypoints = waypoints.map(wp => {
      if (wp.id === id) {
        const updated = { ...wp, ...updates };
        const index = waypoints.findIndex(w => w.id === id);
        if (index > 0) {
          const prevWp = waypoints[index - 1];
          updated.distance = calculateDistance(prevWp.lat, prevWp.lng, updated.lat, updated.lng);
          updated.bearing = calculateBearing(prevWp.lat, prevWp.lng, updated.lat, updated.lng);
        }
        return updated;
      }
      return wp;
    });
    setWaypoints(updatedWaypoints);
  };
  const exportPlan = () => {
    if (!selectedPlan) {
      alert('Please select a plan to export');
      return;
    }
    exportPassagePlan(selectedPlan, waypoints, routeStats, currentVessel);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2">
        {/* Waypoints List */}
        <div className="bg-white shadow rounded-lg">
          <WaypointsList
            waypoints={waypoints}
            isEditing={isEditing}
            onAddWaypoint={() => setShowManualEntry(true)}
            onRemoveWaypoint={removeWaypoint}
            onClearAll={() => setWaypoints([])}
          />
        </div>

        {/* Interactive Chart */}
        <div className="mt-4">
          <PassageChart
            waypoints={waypoints}
            onMapClick={isEditing ? handleMapClick : null}
            onWaypointUpdate={isEditing ? handleWaypointUpdate : null}
            onMapReady={handleMapReady}
            mapError={mapError}
            setMapError={setMapError}
          />
        </div>
      </div>

      {/* Sidebar - 1/3 width */}
      <div className="lg:col-span-1">
        {/* Plan Selector */}
        <div className="bg-white shadow rounded-lg mb-4">
          <div className={DESIGN.header.padding + ' ' + DESIGN.header.borderBottom}>
            <h3 className={DESIGN.header.title}>Passage Plans</h3>
          </div>
          <div className={DESIGN.body.padding}>
            <select
              value={selectedPlan?.id || ''}
              onChange={(e) => {
                if (e.target.value === 'new') {
                  setSelectedPlan(null);
                  setPlanForm({ name: '', vesselSpeed: currentVessel?.cruisingSpeed || 8, notes: '' });
                  setWaypoints([]);
                  setIsEditing(true);
                } else if (e.target.value) {
                  const plan = passagePlans.find(p => p.id === e.target.value);
                  if (plan) loadPlan(plan);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Select a plan...</option>
              <option value="new">+ Create New Plan</option>
              {passagePlans.map(plan => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
            
            {selectedPlan && !isEditing && (
              <div className="mt-4 space-y-2">
                <div className="flex gap-2">
                  <Button color="indigo" className="flex-1" onClick={() => setIsEditing(true)}>
                    Edit
                  </Button>
                  <Button color="white" className="flex-1" onClick={() => onDeletePlan(selectedPlan.id)}>
                    Delete
                  </Button>
                </div>
                <Button color="white" className="w-full" onClick={exportPlan}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Plan
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Plan Details */}
        {(selectedPlan || isEditing) && (
          <PlanDetailsForm
            planForm={planForm}
            setPlanForm={setPlanForm}
            isEditing={isEditing}
            onSave={handleSavePlan}
            onCancel={() => {
              setIsEditing(false);
              if (selectedPlan) loadPlan(selectedPlan);
            }}
            selectedPlan={selectedPlan}
          />
        )}

        {/* Route Summary */}
        {waypoints.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Route Summary</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Waypoints:</span>
                  <span className="font-medium">{routeStats.waypoints}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Total Distance:</span>
                  <span className="font-medium">{routeStats.totalDistance.toFixed(1)} nm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Estimated Time:</span>
                  <span className="font-medium">{formatDuration(routeStats.totalTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">ETA:</span>
                  <span className="font-medium">
                    {routeStats.totalTime > 0
                      ? new Date(Date.now() + routeStats.totalTime * 3600000).toLocaleString()
                      : '--'}
                  </span>
                </div>
              </div>
              
              {selectedPlan && onLinkToVoyage && (
                <Button color="green" className="w-full mt-4" onClick={() => onLinkToVoyage(selectedPlan)}>
                  <Navigation className="h-4 w-4 mr-2" />
                  Use for Voyage
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual Waypoint Entry Modal */}
      <ManualWaypointModal
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        manualWaypoint={manualWaypoint}
        setManualWaypoint={setManualWaypoint}
        waypointsLength={waypoints.length}
        onSave={handleAddManualWaypoint}
      />
    </div>
  );
};
export default PassagePlanningTab;